import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Button, Form, Row, Col } from 'grab-ui';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import {
  SCRIBE_SHOPIFY_STATES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import {
  initConnection,
  updateShopifyConfigurationBasic,
} from 'actions/shopify-endpoints';
import { getCompanyID } from 'helper/user';
import useNotification, {
  makeErrorMessage,
} from 'views/v2/shopify-home/hooks/useNotification';
import { ShopContext } from 'views/v2/shopify-home/contexts/shop-context';
import { generateLocalisedItemOptions } from 'helper/order';
import { pushNotificationAction } from 'actions/app-actions';
import ItemWeightField from './item-weight-field';
// eslint-disable-next-line import/no-named-as-default
import ProductCategoryField from './product-category-field';
import StoreUrlField from './store-url-field';
import styles from './shop-basic-settings-form.less';

const localeKeyPrefix = 'shopify.settings.basicSettingsSection.form';

export const MODE = {
  READ: 'READ',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
};

const FIELD_NAME = {
  STORE_URL: 'Store URL',
  PRODUCT_CATEGORY: 'Product Category',
  DEFAULT_ITEM_WEIGHT: 'Item Weight',
};

const makeProductCategoryOptions = ({ userInfo, pushNotification }) =>
  generateLocalisedItemOptions({
    userInfo,
    pushNotification,
  }).map(({ iString, id }) => ({
    value: id,
    child: iString,
    title: iString,
  }));

const ShopBasicSettingsForm = ({
  dispatch,
  form,
  initialMode,
  loading,
  setLoading,
  pushNotification,
}) => {
  const trackCtx = React.useContext(ShopifyTrackEventContext);

  const fireLinkStoreUrlTrackEvent = newStoreUrl => {
    trackCtx.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.LINK_STORE_URL,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.NEW_STORE_URL]: newStoreUrl,
      },
    });
  };

  const fireLinkStoreFailureTrackEvent = errorMessage => {
    trackCtx.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.SHOPIFY_LINK_FAILURE,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.ERROR_MESSAGE]: errorMessage,
      },
    });
  };

  const { shop, userInfo, refetchShops } = React.useContext(ShopContext);
  const { showNotification } = useNotification(dispatch);
  const [mode, setMode] = React.useState(initialMode);
  const productCategoryOptions = useMemo(
    () => makeProductCategoryOptions({ userInfo, pushNotification }),
    [{ userInfo, pushNotification }],
  );

  const isCreateMode = mode === MODE.CREATE;

  const formInitialValues =
    isCreateMode || !shop
      ? {}
      : // eslint-disable-next-line prettier/prettier
        {
          shop: shop.shop,
          productCategoryID: shop.productCategoryID,
          defaultItemWeight: shop.defaultItemWeight,
        };

  const linkNewShopHandler = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) return;
      setLoading(true);

      const shopIdentifier = values[FIELD_NAME.STORE_URL];
      const timestamp = Date.now();
      const hmac = '';
      initConnection(shopIdentifier, '', timestamp, hmac, true, true)
        .then(({ authorizeURL }) => {
          if (authorizeURL) {
            fireLinkStoreUrlTrackEvent(shopIdentifier);
            window.location.href = authorizeURL;
            return;
          }
          fireLinkStoreFailureTrackEvent();
        })
        .catch(async err => {
          const { message } = await err.json();
          fireLinkStoreFailureTrackEvent(message);
          showNotification(makeErrorMessage(message));
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const saveConfigurationBasicHandler = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) return;
      setLoading(true);

      const companyID = getCompanyID(userInfo) || null;

      const requestParams = {
        shop: values[FIELD_NAME.STORE_URL],
        productCategoryID: parseFloat(values[FIELD_NAME.PRODUCT_CATEGORY]),
        defaultItemWeight: parseFloat(values[FIELD_NAME.DEFAULT_ITEM_WEIGHT]),
      };

      updateShopifyConfigurationBasic(requestParams, companyID)
        .then(success => {
          if (!success) {
            showNotification(
              makeErrorMessage('Settings not updated. An error has occurred.'),
            );
            return;
          }
          refetchShops();
          setMode(MODE.READ);
        })
        .catch(async err => {
          const { message } = await err.json();
          showNotification(makeErrorMessage(message));
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const noopHandler = e => {
    e.preventDefault();
  };

  const makeSubmitHandler = mode => {
    switch (mode) {
      case MODE.CREATE:
        return linkNewShopHandler;
      case MODE.EDIT:
        return saveConfigurationBasicHandler;
      case MODE.READ:
        return noopHandler;
    }
  };

  const makeActionButtons = (mode, disabled) => {
    switch (mode) {
      case MODE.CREATE:
        return [
          <Button
            key={1}
            type="link"
            htmlType="submit"
            className={styles.ActionButton}
            disabled={disabled}
          >
            {I18n.t('v2.commonButton.link')}
          </Button>,
        ];
      case MODE.EDIT:
        return [
          <Button
            key={1}
            type="link"
            htmlType="submit"
            className={styles.ActionButton}
            disabled={disabled}
          >
            {I18n.t('v2.commonButton.save')}
          </Button>,
          <Button
            key={2}
            type="link"
            className={styles.ActionButton}
            disabled={disabled}
            onClick={() => {
              form.resetFields();
              setMode(MODE.READ);
            }}
          >
            {I18n.t('v2.commonButton.cancel')}
          </Button>,
        ];
      case MODE.READ:
        return [
          <Button
            key={1}
            type="link"
            className={styles.ActionButton}
            onClick={e => {
              // prevent invoking form submit
              e.preventDefault();
              setMode(MODE.EDIT);
            }}
          >
            {I18n.t('v2.commonButton.edit')}
          </Button>,
        ];
    }
  };

  React.useEffect(
    () => {
      if (mode === MODE.EDIT) {
        form.validateFields([FIELD_NAME.PRODUCT_CATEGORY]);
      }
    },
    [mode],
  );

  return (
    <Form
      className={styles.ShopSettingsForm}
      onSubmit={makeSubmitHandler(mode)}
    >
      <Row type="flex" gutter={16}>
        <Col className={styles.mt16} xs={24} md={8}>
          <div className={styles.FieldName}>
            {I18n.t(`${localeKeyPrefix}.fieldLabel.url`)}
          </div>
          {mode === MODE.READ ? (
            <p>{formInitialValues.shop}</p>
          ) : (
            <StoreUrlField
              id={FIELD_NAME.STORE_URL}
              initialValue={formInitialValues.shop || ''}
              disabled={mode === MODE.EDIT || loading}
              form={form}
            />
          )}
        </Col>
        {!isCreateMode && [
          <Col key={1} className={styles.mt16} xs={24} md={8}>
            <div className={styles.FieldName}>
              {I18n.t(`${localeKeyPrefix}.fieldLabel.productCategory`)}
            </div>
            {mode === MODE.READ ? (
              <p>
                {_get(
                  productCategoryOptions.find(
                    o => o.value === formInitialValues.productCategoryID,
                  ),
                  'title',
                )}
              </p>
            ) : (
              <ProductCategoryField
                id={FIELD_NAME.PRODUCT_CATEGORY}
                initialValue={formInitialValues.productCategoryID}
                disabled={loading}
                form={form}
                productCategoryOptions={productCategoryOptions}
              />
            )}
          </Col>,
          <Col key={2} className={styles.mt16} xs={24} md={8}>
            <div className={styles.FieldName}>
              {I18n.t(`${localeKeyPrefix}.fieldLabel.itemWeight`)}
            </div>
            {mode === MODE.READ ? (
              <p>{formInitialValues.defaultItemWeight}</p>
            ) : (
              <ItemWeightField
                id={FIELD_NAME.DEFAULT_ITEM_WEIGHT}
                initialValue={
                  formInitialValues.defaultItemWeight
                    ? formInitialValues.defaultItemWeight.toString()
                    : ''
                }
                disabled={loading}
                form={form}
              />
            )}
          </Col>,
        ]}
      </Row>
      <div className={styles.ActionButtonsContainer}>
        {makeActionButtons(mode, loading)}
      </div>
    </Form>
  );
};

ShopBasicSettingsForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  initialMode: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  pushNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  pushNotification: pushNotificationAction,
};

export default connect(null, mapDispatchToProps)(
  Form.create()(ShopBasicSettingsForm),
);
